// src/hooks/useGeneralTestLogic.js
import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import { setTestResults, addReferedBy } from "../Redux_Services/slices/testSlice";
import { useNavigate } from "react-router-dom";
import {CALCULATED_TESTS} from "../Utils/TestNames"

// Keep linked ParameterIDs in sync (e.g., Hemoglobin duplicate fields)
const LINKED_RESULT_GROUPS = [
  ["BL9029", "BL9030"],
];

// Define dependent test formulas


// Utility to cascade compute dependent values until stable
function cascadeCompute(initial) {
  const result = { ...initial };
  let changed;
  do {
    changed = false;
    for (const [child, { dependsOn, formula }] of Object.entries(CALCULATED_TESTS)) {
      if (dependsOn.every((dep) => result[dep] !== "" && !isNaN(result[dep]))) {
        const computed = formula(result);
        if (result[child] !== computed) {
          result[child] = computed;
          changed = true;
        }
      }
    }
  } while (changed);
  return result;
}

// Normalize linked ids so they always mirror each other
const normalizeLinkedGroups = (draft) => {
  const next = { ...draft };
  LINKED_RESULT_GROUPS.forEach((group) => {
    const sharedValue = group
      .map((id) => next[id])
      .find((val) => val !== undefined && val !== null && val !== "");
    if (sharedValue !== undefined) {
      group.forEach((id) => {
        next[id] = sharedValue;
      });
    }
  });
  return next;
};

export function useGeneralTestLogic(tests, initialReferredBy) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [results, setResults] = useState({});
  const [referredBy, setReferredBy] = useState(initialReferredBy);

  const getSubTests = useCallback((opt = {}) => {
    if (Array.isArray(opt.subTests)) return opt.subTests;
    if (Array.isArray(opt.subTest)) return opt.subTest;
    return [];
  }, []);

  // Initialize and precompute on tests change
  useEffect(() => {
    const init = {};
    const setInitialValue = (id, value) => {
      if (!id) return;
      init[id] = value ?? "";
    };

    tests.forEach((group) => {
      group.options?.forEach((opt) => {
        setInitialValue(opt.ParameterID, opt.result);
        getSubTests(opt).forEach((sub) => {
          const subId = sub?.ParameterID || sub?.id;
          setInitialValue(subId, sub?.result ?? sub?.value);
        });
      });
    });
    setResults(cascadeCompute(normalizeLinkedGroups(init)));
  }, [tests, getSubTests]);

  // Handle change and cascade recompute
  const handleResultChange = useCallback((id, val) => {
    setResults((prev) => {
      const base = { ...prev };
      const linkedGroup = LINKED_RESULT_GROUPS.find((group) =>
        group.includes(id)
      );

      if (linkedGroup) {
        linkedGroup.forEach((linkedId) => {
          base[linkedId] = val;
        });
      } else {
        base[id] = val;
      }

      return cascadeCompute(normalizeLinkedGroups(base));
    });
  }, []);

  // Validation: all results non-empty
  const allResultsFilled = useMemo(() => {
    const hasValue = (val) =>
      val !== null &&
      val !== undefined &&
      val.toString().trim() !== "" &&
      val.toString().trim().toLowerCase() !== "n/a";

    const isOptionFilled = (opt = {}) => {
      if (hasValue(results[opt.ParameterID])) return true;

      const subs = getSubTests(opt);

      if (subs.length === 0) return false;

      const subHasValue = (sub) => {
        const subId = sub?.ParameterID || sub?.id;
        if (!subId) return false;
        return hasValue(results[subId]);
      };

      // For glucose we only need any one of the sub-tests filled
      if (opt?.ParameterID === "DI9470") {
        return subs.some(subHasValue);
      }

      return subs.every(subHasValue);
    };

    return (
      Array.isArray(tests) &&
      tests.length > 0 &&
      tests.every(
        (group) =>
          Array.isArray(group?.options) &&
          group.options.every((opt) => isOptionFilled(opt))
      )
    );
  }, [tests, results, getSubTests]);

  // Save handlers
  const saveResults = useCallback(
    (options = {}) => {
      const { redirectToReport = true } = options || {};

      // Persist referredBy so Report page can read it from localStorage
      dispatch(addReferedBy(referredBy || ""));
      dispatch(setTestResults(results));
      // console.log("---result--" , results)

      if (redirectToReport) {
        navigate("/tests/testId/report", { state: { fromGeneralPage: true } });
      }
    },
    [dispatch, navigate, referredBy, results]
  );

  const saveReferredBy = useCallback(() => {
    dispatch(addReferedBy(referredBy));
  }, [dispatch, referredBy]);

  return {
    results,
    referredBy,
    setReferredBy,
    handleResultChange,
    allResultsFilled,
    saveResults,
    saveReferredBy,
  
  };
}
